import React from 'react';
import classNames from 'classnames';
import { useTranslation, Trans } from 'react-i18next';
import styles from './DynamicSizingGuide.module.scss';
import { type BreedWeight } from './BreedDataset';
import { SizeOption } from '../../../../types';
import { F3BandSizeOption, WeightUnit, f3LargerSize } from '../../../../lib/size';
import { distributionRows } from './sizeFromDistribution';

interface DynamicSizingGuideResultProps {
  sizingGuideItem: BreedWeight;
  breedName?: string;
  isPuppy: boolean;
  currentWeight: number;
  weightUnit: WeightUnit;
  onSelect: (newValue: SizeOption) => void;
  /** Floor from the outcome-based weight bands; see f3MinimumSizeForWeight. */
  minimumSize?: SizeOption;
  /** Whether the floor came from the dog's head/neck geometry, which changes how we explain it. */
  hasWideNeck?: boolean;
}

const sizeLabel = (size: SizeOption, t: (key: string) => string): string => {
  if (size === SizeOption.XLarge) {
    return t('x_large');
  } else if (size === SizeOption.Large) {
    return t('large');
  } else if (size === SizeOption.Medium) {
    return t('medium');
  } else if (size === SizeOption.Small) {
    return t('small');
  } else if (size === SizeOption.XSmall) {
    return t('x_small');
  } else {
    return t('unknown');
  }
};

export default function DynamicSizingGuideResult({
  sizingGuideItem,
  breedName,
  isPuppy,
  currentWeight,
  weightUnit,
  onSelect,
  minimumSize,
  hasWideNeck,
}: DynamicSizingGuideResultProps) {
  const { t } = useTranslation();
  // The breakdown stays factual — it reports what other owners bought. What we *recommend* is floored
  // at the weight band, so the guide can never point below the size card for this weight.
  const rows = distributionRows(sizingGuideItem);
  const distributionSize = rows.length > 0 ? rows[0].size : null;
  const sizeOption =
    distributionSize && minimumSize
      ? f3LargerSize(distributionSize as F3BandSizeOption, minimumSize as F3BandSizeOption)
      : distributionSize;
  // The breakdown can say one size while the button says another whenever the floor bites. Say why,
  // and only then — a caveat shown on every result is the one shoppers learn to skip.
  const floorOverridesDistribution = !!sizeOption && !!distributionSize && sizeOption !== distributionSize;
  // A tie has no winner to announce, and the widest bar is the reference the rest are read against.
  const widestPercentage = rows.length > 0 ? rows[0].percentage : 0;
  // The recommended size leads and is the only filled bar. Ranked purely by popularity it sat below
  // a size we are not recommending — a 30 lb Frenchie showed Large at 44% under Medium at 50%, which
  // reads as an argument against the answer directly above it.
  const orderedRows = [
    ...rows.filter((row) => row.size === sizeOption),
    ...rows.filter((row) => row.size !== sizeOption),
  ];

  if (!sizeOption) return null;

  return (
    <>
      <div className={styles.sizingGuideResultContainer}>
        {/* The answer leads. The breakdown below is context for it, not a competing headline. */}
        <div className={styles.recommendationEyebrow}>{t('we_recommend_size')}</div>
        <div className={styles.recommendationSize}>{sizeLabel(sizeOption, t)}</div>
        {floorOverridesDistribution && (
          <div className={styles.sizeUpReason}>
            <Trans
              i18nKey={
                hasWideNeck
                  ? 'breed_runs_a_size_up_we_recommend_size_rather_than_size'
                  : 'we_recommend_size_owners_who_bought_size_at_this_weight_exchanged_far_more_often'
              }
              values={{
                breed: breedName,
                recommended: sizeLabel(sizeOption, t),
                distribution: distributionSize ? sizeLabel(distributionSize, t) : '',
              }}
              components={{ rec: <span className={styles.sizeUpReasonSize} /> }}
            />
          </div>
        )}
        {!isPuppy && (
          <div className={styles.distributionSection}>
            <div className={styles.distributionHeading}>
              <Trans
                i18nKey={
                  breedName
                    ? weightUnit === 'kg'
                      ? 'collar_sizes_of_weight_kg_breed_on_fi'
                      : 'collar_sizes_of_weight_lb_breed_on_fi'
                    : weightUnit === 'kg'
                      ? 'collar_sizes_of_weight_kg_dogs_on_fi'
                      : 'collar_sizes_of_weight_lb_dogs_on_fi'
                }
                values={{ weight: currentWeight, breed: breedName }}
                components={{
                  weight: <span className={styles.distributionHeadingBoldText} />,
                  breed: <span className={styles.distributionHeadingBoldText} />,
                }}
              />
            </div>
            {orderedRows.map((row) => {
              const isRecommended = row.size === sizeOption;
              return (
                <div
                  className={classNames(styles.distributionRow, {
                    [styles.distributionRowRecommended]: isRecommended,
                  })}
                  key={row.size}
                >
                  <div className={styles.distributionRowSize}>{sizeLabel(row.size, t)}</div>
                  <div className={styles.distributionRowTrack}>
                    <div
                      className={styles.distributionRowBar}
                      style={{ width: `${widestPercentage > 0 ? (row.percentage / widestPercentage) * 100 : 0}%` }}
                    />
                  </div>
                  <div className={styles.distributionRowPercentage}>
                    {row.percentage}
                    {'%'}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <button className={styles.selectSizeButton} onClick={() => onSelect(sizeOption)}>
          {t('select_size', { size: sizeLabel(sizeOption, t) })}
        </button>
      </div>
      {isPuppy && (
        <div className={styles.puppySpecialOfferSection}>
          <div className={styles.puppySpecialOfferTitle}>
            <div className={styles.pawIcon} />
            {t('fi_puppy_warranty')}
          </div>
          <div className={styles.puppySpecialOfferDescription}>
            <Trans
              i18nKey="fi_series_3_plus_is_our_most_durable_longest_lasting_wearable_get_a_size_collar_now_and_well_swap_it_out_for_free_when_they_are_fully_grown"
              values={{
                size: sizeLabel(sizeOption, t).toLowerCase(),
              }}
              components={{
                sizeSpan: <span />,
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
